"use client";

import { WIDGET_PATH_PREFIXES } from "@/lib/onboarding/widget-paths";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { usePathname } from "next/navigation";
import { unprefixedPath } from "@/lib/i18n/switch-locale";
import { MinusIcon } from "lucide-react";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { MAX_USER_TURNS, type Handoff } from "@/lib/onboarding/discovery";
import {
  clearPreauthStash,
  loadPreauthStash,
  savePreauthStash,
  type PreauthStashMessage,
} from "@/lib/onboarding/preauth-stash";
import { renderChatMarkdown } from "@/lib/markdown";
import { MAX_BOT_NAME_LENGTH, normalizeBotDisplayName } from "@/lib/bot-name";
import { trackEvent } from "@/lib/analytics";
import { TrackedSignupLink } from "@/components/tracked-signup-link";
import { TYPE_BLURBS, TYPE_LABELS, TYPE_ORDER } from "@/components/onboarding/handoff-card";
import { TYPE_LABEL_MSGS } from "@/lib/agent-type-labels";
import { SquidlerIcon } from "@/components/squidler-logo";
import { truncateChars } from "@shared/text";
import { MAINTENANCE_ERROR_CODE } from "@shared/maintenance";

interface ChatMessage {
  role: "user" | "assistant";
  text: string;
}

/** Last-resort handoff when the capped final turn still yields no structured
 *  handoff — mirrors the authed flow's fallback; the transcript is replayed
 *  into the hired agent at claim time regardless. User-visible fields are
 *  Lingui descriptors, resolved per-locale at render. */
const FALLBACK_PROBLEM = msg`Let's get you set up — I'll pick up the details from our conversation.`;
const FALLBACK_TYPE_REASON = msg`Starting you with a personal assistant — switch the type below if another fits better.`;

/** `round` for the widget-chrome placements ("Set up your Hire" header + the
 *  launcher pill); message rows keep the square app-avatar look. */
function OnboardingAvatar({ round = false }: { round?: boolean }) {
  return (
    <div className={`w-7 h-7 ${round ? "rounded-full" : "rounded-lg"} bg-primary flex items-center justify-center shrink-0 mt-0.5`} aria-hidden>
      <SquidlerIcon className="h-4 w-auto text-primary-foreground" />
    </div>
  );
}

/**
 * The pre-auth handoff card: same summary as the authed review card, but the
 * primary action is the SIGNUP — the account doesn't exist yet, so there is
 * no availability check (the post-signup claim re-resolves the name, with a
 * suffix fallback, before the atomic hire).
 */
function PreauthHandoffCard({
  botName,
  onNameChange,
  suggestion,
  chosenType,
  onChooseType,
  signupHref,
  trial,
}: {
  botName: string;
  onNameChange: (name: string) => void;
  suggestion: Handoff;
  chosenType: string;
  onChooseType: (slug: string) => void;
  signupHref: string;
  trial: { window: string; worth: string } | null;
}) {
  const { t, i18n } = useLingui();
  const [showRoles, setShowRoles] = useState(false);
  const isSuggested = chosenType === suggestion.agent_type;
  const trimmedName = botName.trim();
  return (
    <div className="rounded-2xl border border-border bg-surface-elevated shadow-sm p-5 space-y-4">
      <div className="space-y-3">
        {suggestion.problem && (
          <div>
            <div className="text-xs text-foreground-muted mb-0.5"><Trans>What your Hire will help with</Trans></div>
            <div className="text-sm text-foreground">{suggestion.problem}</div>
          </div>
        )}
        {suggestion.proposed_first_action && (
          <div>
            <div className="text-xs text-foreground-muted mb-0.5"><Trans>First step</Trans></div>
            <div className="text-sm text-foreground">{suggestion.proposed_first_action}</div>
          </div>
        )}
      </div>

      <div className="space-y-1.5">
        <label htmlFor="preauth-hire-name" className="text-xs text-foreground-muted"><Trans>Name your Hire</Trans></label>
        <input
          id="preauth-hire-name"
          value={botName}
          onChange={(e) => { onNameChange(e.target.value); }}
          maxLength={MAX_BOT_NAME_LENGTH}
          className="w-full rounded-xl border border-border bg-surface px-3 py-2 text-foreground outline-none focus:border-primary/60 focus:ring-2 focus:ring-primary/30 transition"
        />
      </div>

      <div>
        <div className="text-sm text-foreground">
          <Trans>Set up as a <span className="font-semibold text-primary">{i18n._(TYPE_LABEL_MSGS[chosenType] ?? TYPE_LABEL_MSGS.pa)}</span>.</Trans>{" "}
          <button
            onClick={() => { setShowRoles((o) => !o); }}
            className="text-xs text-foreground-muted hover:text-foreground underline underline-offset-2"
          >
            {showRoles ? <Trans>Hide other roles ▴</Trans> : <Trans>See other roles ▾</Trans>}
          </button>
        </div>
        <div className="text-xs text-foreground-muted mt-1">
          {isSuggested && suggestion.type_reason ? suggestion.type_reason : i18n._(TYPE_BLURBS[chosenType])}
        </div>

        {showRoles && (
          <div className="grid grid-cols-2 gap-2 mt-3">
            {TYPE_ORDER.map((slug) => (
              <button
                key={slug}
                onClick={() => { onChooseType(slug); setShowRoles(false); }}
                className={`text-left px-3 py-2 rounded-lg border text-sm transition ${chosenType === slug ? "border-primary bg-primary-soft text-foreground" : "border-border bg-surface-elevated text-foreground hover:bg-surface-sunken"}`}
              >
                <div className="font-medium">{i18n._(TYPE_LABEL_MSGS[slug])}</div>
                <div className="text-[11px] text-foreground-muted mt-0.5">{i18n._(TYPE_BLURBS[slug])}</div>
                {slug === suggestion.agent_type && <div className="text-[10px] text-primary mt-0.5"><Trans>suggested</Trans></div>}
              </button>
            ))}
          </div>
        )}
      </div>

      <TrackedSignupLink
        href={signupHref}
        intent="signup"
        source="preauth-handoff"
        className="block w-full px-4 py-3 rounded-xl bg-primary text-primary-foreground font-medium text-center hover:bg-primary-hover transition"
      >
        {trimmedName ? t`Create free account & meet ${trimmedName}` : t`Create free account & meet your Hire`}
      </TrackedSignupLink>
      <p className="text-xs text-center text-foreground-muted">
        {trial
          ? t`${trial.window} free with a welcome bonus of credits worth ${trial.worth} — no card required.`
          : t`Takes seconds — no card required.`}
      </p>
    </div>
  );
}

interface PreauthOnboardingProps {
  /** Random pool name pre-filling the handoff card (editable there; the
   *  post-signup claim re-resolves availability). */
  suggestedName: string;
  /** BCP-47 tag from Accept-Language — shapes the opening seed only. */
  languageHint?: string;
  /** Live trial offer, quoted on the handoff card's fine print. */
  trial: { window: string; worth: string } | null;
  /** When set, the header shows a minimize control calling this — provided by
   *  PreauthChatDock only in floating (scrolled-away) mode. */
  onMinimize?: () => void;
}

/**
 * The landing-page discovery chat (landing_onboarding "onboarding" arm) — the
 * authed OnboardingFlow's streaming/typewriter mechanics, embedded as a hero
 * panel for ANONYMOUS visitors. Talks to /api/onboarding/preauth (no auth, no
 * DB persistence); the transcript lives in sessionStorage (see preauth-stash)
 * and the handoff card's CTA is the Auth0 signup, carrying the session id so
 * /api/signup-redirect can route into the claim.
 */
export function PreauthOnboarding({ suggestedName, languageHint, trial, onMinimize }: PreauthOnboardingProps) {
  const { t, i18n } = useLingui();
  const fallbackHandoff = useMemo((): Handoff => ({
    problem: i18n._(FALLBACK_PROBLEM),
    proposed_first_action: "",
    promises_made: [],
    fit: "weak",
    agent_type: "pa",
    type_reason: i18n._(FALLBACK_TYPE_REASON),
  }), [i18n]);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [userTurns, setUserTurns] = useState(0);
  const [suggestion, setSuggestion] = useState<Handoff | null>(null);
  const [chosenType, setChosenType] = useState<string>("pa");
  const [botName, setBotName] = useState(suggestedName);
  // True once the visitor typed in the name field themselves — a handoff's
  // requested_agent_name pre-fills the card only while this is false (mirrors
  // the authed flow).
  const [nameEdited, setNameEdited] = useState(false);
  const [restoreChecked, setRestoreChecked] = useState(false);

  const sessionId = useRef<string>("");
  if (!sessionId.current && typeof crypto !== "undefined") sessionId.current = crypto.randomUUID();
  const startedRef = useRef(false);
  const lastTurnRef = useRef<{ history: ChatMessage[]; force: boolean } | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Stream one discovery reply — the same NDJSON protocol + eased typewriter
  // reveal as the authed flow (see onboarding-flow.tsx for the mechanics
  // rationale; the preparing-without-handoff recovery replay included).
  const streamReply = useCallback(
    async (history: ChatMessage[], force: boolean) => {
      lastTurnRef.current = { history, force };
      setBusy(true);
      setError(null);
      setPreparing(false);
      let target = "";
      let shown = 0;
      let streamDone = false;
      let aborted = false;
      let pendingHandoff: Handoff | null = null;
      let sawPreparing = false;
      setMessages([...history, { role: "assistant", text: "" }]);

      let last = performance.now();
      const userTurnCount = history.filter((m) => m.role === "user").length;
      const tick = (ts: number) => {
        if (aborted) return;
        const dt = (ts - last) / 1000;
        last = ts;
        shown = Math.min(target.length, shown + Math.ceil(90 * dt));
        const done = streamDone && shown >= target.length;
        // A text-less turn (a natural handoff) commits NO assistant message —
        // the card is the visual, and a placeholder would flow into the claim
        // transcript and the hired agent's replayed history.
        setMessages(target || !done ? [...history, { role: "assistant", text: truncateChars(target, shown) }] : history);
        if (done) {
          setBusy(false);
          setPreparing(false);
          if (pendingHandoff) {
            const h = pendingHandoff;
            setChosenType((h.agent_type && TYPE_LABELS[h.agent_type]) ? h.agent_type : "pa");
            setSuggestion(h);
            // A name the user asked for mid-chat beats the random suggestion —
            // never a hand-typed one (mirrors the authed flow).
            const requested = h.requested_agent_name ? normalizeBotDisplayName(h.requested_agent_name) : undefined;
            if (requested && !nameEdited) setBotName(requested);
            trackEvent("Handoff Shown", { mode: "preauth", agent_type: h.agent_type, fallback: false, user_turns: userTurnCount, forced: force });
          } else if (force) {
            setChosenType(fallbackHandoff.agent_type);
            setSuggestion(fallbackHandoff);
            trackEvent("Handoff Shown", { mode: "preauth", agent_type: fallbackHandoff.agent_type, fallback: true, user_turns: userTurnCount, forced: force });
          } else if (sawPreparing) {
            trackEvent("Handoff Recovery", { mode: "preauth", user_turns: userTurnCount });
            void streamReply(history, true);
          }
          return;
        }
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);

      try {
        const res = await fetch("/api/onboarding/preauth", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sessionId: sessionId.current,
            messages: history.map((m) => ({ role: m.role, text: m.text })),
            languageHint,
            force,
          }),
        });
        if (!res.ok || !res.body) {
          const msg = await res
            .json()
            .then((d) => (typeof d?.error === "string" ? d.error : null))
            .catch(() => null);
          // Platform maintenance window (lib/maintenance.ts): a friendly hold
          // beats surfacing the raw error code.
          if (res.status === 503 && msg === MAINTENANCE_ERROR_CODE) {
            throw new Error(t`We're doing planned maintenance right now — please try again a little later.`);
          }
          throw new Error(msg || t`Something hiccuped on our end — tap Try again.`);
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = "";
        let sawDone = false;
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const lines = buf.split("\n");
          buf = lines.pop() ?? "";
          for (const ln of lines) {
            if (!ln.trim()) continue;
            const ev = JSON.parse(ln);
            if (ev.type === "text") {
              const delta: unknown = ev.delta;
              if (typeof delta === "string") target += delta;
            }
            else if (ev.type === "preparing") { sawPreparing = true; setPreparing(true); }
            else if (ev.type === "handoff") pendingHandoff = ev.payload;
            else if (ev.type === "done") sawDone = true;
            else if (ev.type === "error") throw new Error(ev.message || t`The model hit an error — tap Try again.`);
          }
        }
        if (!sawDone) {
          // A severed stream must never end as a silent partial bubble — see
          // the authed flow's identical guard (prod incident 2026-07-07).
          throw new Error(t`The connection dropped mid-reply — tap Try again.`);
        }
        streamDone = true;
      } catch (e) {
        aborted = true;
        setError(e instanceof Error ? e.message : String(e));
        setMessages(history);
        setBusy(false);
        setPreparing(false);
      }
    },
    [languageHint, nameEdited, fallbackHandoff, t],
  );

  const autoStart = useCallback(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    void streamReply([], false);
  }, [streamReply]);

  // Restore a same-tab session (reload, or backing out of the Auth0 screen)
  // before anything streams. A restored history ending on a user message means
  // the reply never landed — replay that turn; ending on an assistant message
  // just waits for the visitor's reply.
  useEffect(() => {
    // Unadvertised reset (?preauth_reset=1, same spirit as the ?exp_ preview
    // overrides): wipe the stash and start a fresh session — for QA'ing the
    // opener without hunting down the sessionStorage key.
    if (new URLSearchParams(window.location.search).has("preauth_reset")) {
      clearPreauthStash();
    }
    const stored = loadPreauthStash();
    // Language-switch rule: a chat the visitor has ENGAGED with (typed
    // anything) is theirs — never altered by a language switch. But an
    // unengaged stash (opener only) is discarded when the site language no
    // longer matches, so the greeting regenerates in the newly chosen
    // language instead of replaying the old one.
    const engaged = stored !== null
      && (stored.userTurns > 0 || stored.messages.some((m) => m.role === "user"));
    const languageSwitched = stored !== null && !engaged
      && (stored.language ?? undefined) !== (languageHint ?? undefined);
    if (languageSwitched) clearPreauthStash();
    const stash = languageSwitched ? null : stored;
    if (stash) {
      sessionId.current = stash.sessionId;
      startedRef.current = true;
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot mount restore from sessionStorage (an external system unavailable during SSR); runs before anything streams, so the single cascading render is the point
      setBotName(stash.botName || suggestedName);
      setChosenType(stash.chosenType || "pa");
      setUserTurns(stash.userTurns);
      setMessages(stash.messages);
      setSuggestion(stash.suggestion);
      if (!stash.suggestion && stash.messages.length > 0 && stash.messages[stash.messages.length - 1].role === "user") {
        void streamReply(stash.messages, stash.userTurns >= MAX_USER_TURNS);
      }
    }
    setRestoreChecked(true);
    // Mount-only: restore must run exactly once, before the autoStart effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (restoreChecked && !startedRef.current) autoStart();
  }, [restoreChecked, autoStart]);

  // Keep the stash current — it IS the persistence layer (and the claim
  // payload the post-signup page reads). Not while a reply is mid-stream:
  // the partial assistant bubble would replay as a full turn.
  useEffect(() => {
    if (!startedRef.current || busy || (messages.length === 0 && !suggestion)) return;
    savePreauthStash({
      sessionId: sessionId.current,
      botName,
      chosenType,
      messages: messages.filter((m): m is PreauthStashMessage => m.text.trim() !== ""),
      userTurns,
      suggestion,
      language: languageHint,
    });
  }, [busy, messages, suggestion, botName, chosenType, userTurns, languageHint]);

  const retry = useCallback(() => {
    if (busy) return;
    setError(null);
    if (messages.length === 0) {
      startedRef.current = false;
      autoStart();
      return;
    }
    const lastTurn = lastTurnRef.current;
    if (lastTurn) void streamReply(lastTurn.history, lastTurn.force);
  }, [busy, messages.length, autoStart, streamReply]);

  const send = useCallback(() => {
    const text = input.trim();
    if (!text || busy || suggestion || !startedRef.current) return;
    setInput("");
    const nextTurns = userTurns + 1;
    setUserTurns(nextTurns);
    void streamReply([...messages, { role: "user", text }], nextTurns >= MAX_USER_TURNS);
  }, [input, busy, suggestion, userTurns, messages, streamReply]);

  // Idle rescue — an engaged visitor who stops replying still deserves the
  // card (same rationale + once-per-session guard as the authed flow).
  const idleForcedRef = useRef(false);
  useEffect(() => {
    if (idleForcedRef.current || busy || preparing || error !== null) return;
    if (suggestion || !startedRef.current || userTurns < 2 || input.trim() !== "") return;
    const t = setTimeout(() => {
      idleForcedRef.current = true;
      trackEvent("Handoff Idle Forced", { mode: "preauth", user_turns: userTurns });
      void streamReply(messages, true);
    }, 120_000);
    return () => { clearTimeout(t); };
  }, [busy, preparing, error, suggestion, userTurns, input, messages, streamReply]);

  // Restore composer focus when a reply settles — the textarea is disabled
  // while streaming, which drops focus, and without this the visitor has to
  // click back into it after every turn. Landing-page etiquette differs from
  // the authed flow's version: preventScroll (a late-settling reply must not
  // yank a visitor who scrolled down to the sections), and on touch devices
  // only after they've typed once (auto-focus on the opener would pop the
  // keyboard over a page they're still reading).
  useEffect(() => {
    if (busy || suggestion || !startedRef.current || messages.length === 0) return;
    if (userTurns === 0 && window.matchMedia("(pointer: coarse)").matches) return;
    inputRef.current?.focus({ preventScroll: true });
  }, [busy, suggestion, messages.length, userTurns]);

  // Auto-grow the composer (cap 128px = the max-h-32 backstop class). Scroll
  // only once the cap is actually hit: below it the field always fits its
  // content, and sub-pixel measurement drift (mobile DPR rounding, the webfont
  // swapping in after mount) must never surface as a phantom scrollbar on a
  // single-line field.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    const borderY = el.offsetHeight - el.clientHeight;
    const target = el.scrollHeight + borderY;
    el.style.height = `${Math.min(target, 128)}px`;
    el.style.overflowY = target > 128 ? "auto" : "hidden";
  }, [input]);

  const signupHref = `/auth/signup?returnTo=${encodeURIComponent(`/api/signup-redirect?onboarding_session=${sessionId.current}`)}`;

  const lastAssistantIdx = messages.reduce((acc, msg, idx) => (msg.role === "assistant" ? idx : acc), -1);

  return (
    // Chat-widget look: a FIXED-palette dark-green title bar (the film hero's
    // deep forest green, film-landing.tsx — same in light and dark) over a
    // white body. The green bar is what stops the panel reading as a static
    // screenshot of the page; the conversation itself stays on light tokens.
    <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-surface-elevated shadow-[0_30px_80px_-40px_rgba(0,0,0,0.45)]">
      <div className="flex items-center gap-2.5 border-b border-[#1a2c20] bg-[linear-gradient(120deg,#3d5f45,#233A29_40%,#1a2c20_70%,#3d5f45)] bg-[length:220%_100%] animate-[sqGradientDrift_9s_ease-in-out_infinite] px-5 py-3.5 shrink-0">
        <OnboardingAvatar round />
        <p className="min-w-0 text-sm font-medium text-cream leading-tight"><Trans>Set up your Hire</Trans></p>
        {onMinimize && (
          <button
            type="button"
            onClick={onMinimize}
            aria-label={t`Minimize chat`}
            className="ml-auto shrink-0 rounded-lg p-1.5 text-cream/70 transition hover:bg-white/10 hover:text-cream"
          >
            <MinusIcon className="size-4" aria-hidden />
          </button>
        )}
      </div>

      {/* column-reverse is the idiomatic CSS chat-pinning trick: with one
          content child, the browser anchors it to the BOTTOM and keeps the
          scroll pinned there through message growth, typewriter reveal,
          restore-on-reload, and the dock morph's resize — no JS scroll
          management. A visitor who scrolls up to reread stays where they are
          (growth extends below without yanking). */}
      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col-reverse">
        <div className="px-5 py-4 space-y-3">
          {messages.length === 0 && !error && (
            <div className="flex items-center gap-2.5 text-sm text-foreground-muted px-1">
              <span className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <Trans>Starting your setup…</Trans>
            </div>
          )}

          {messages.map((m, i) => {
            if (m.role === "user") {
              return (
                <div key={i} className="flex justify-end">
                  <div className="max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap bg-primary-soft text-foreground">
                    {m.text}
                  </div>
                </div>
              );
            }
            // The opener arrives as three short blank-line-separated messages —
            // split into bubbles like the authed flow so it reads as chat.
            const parts = i === 0 && m.text ? m.text.split(/\n{2,}/).filter((p) => p.trim()) : [m.text];
            return parts.map((part, j) => (
              <div key={`${i}-${j}`} className="flex justify-start gap-2">
                {i === lastAssistantIdx && j === parts.length - 1
                  ? <OnboardingAvatar />
                  : <div className="w-7 shrink-0" aria-hidden />}
                <div className="max-w-[85%] rounded-2xl px-4 py-2.5 text-sm bg-surface border border-border-subtle text-foreground">
                  {part ? (
                    <div className="chat-markdown break-words overflow-hidden" dangerouslySetInnerHTML={{ __html: renderChatMarkdown(part) }} />
                  ) : (
                    busy ? "…" : ""
                  )}
                </div>
              </div>
            ));
          })}

          {preparing && !suggestion && (
            <div className="flex items-center gap-2.5 text-sm text-foreground-muted px-1">
              <span className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <Trans>Putting together your setup…</Trans>
            </div>
          )}

          {suggestion && (
            <PreauthHandoffCard
              botName={botName}
              onNameChange={(v) => { setBotName(v); setNameEdited(true); }}
              suggestion={suggestion}
              chosenType={chosenType}
              onChooseType={setChosenType}
              signupHref={signupHref}
              trial={trial}
            />
          )}

          {error && (
            <div className="flex items-center gap-3 text-sm text-destructive">
              <span>⚠ {error}</span>
              {!suggestion && (
                <button
                  onClick={retry}
                  className="shrink-0 rounded-lg border border-border px-3 py-1 text-xs font-medium text-foreground transition hover:bg-surface-sunken"
                >
                  <Trans>Try again</Trans>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {!suggestion && !preparing && (
        <div className="border-t border-border-subtle bg-surface-sunken/60 shrink-0 px-4 py-3 flex gap-2 items-end">
          {/* Fixed-white field with a soft signal glow on a sunken strip —
              unmistakably an input, whatever the site theme. text-base below
              sm: iOS Safari zooms the whole page in on focus of any input
              under 16px, and the zoom sticks after blur (clipped hero, cut-off
              headline). */}
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => { setInput(e.target.value); }}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(); } }}
            enterKeyHint="send"
            disabled={busy || messages.length === 0}
            rows={1}
            placeholder={busy ? "…" : t`Type your reply…`}
            className="flex-1 resize-none max-h-32 overflow-y-hidden rounded-xl border-2 border-transparent bg-white px-4 py-2.5 text-base sm:text-sm text-[#26262b] placeholder:text-[#6b6b70] shadow-[0_0_24px_-4px_rgba(var(--signal-rgb),0.5)] outline-none focus:border-signal/60 focus:ring-2 focus:ring-signal/40 disabled:opacity-60 transition"
          />
          <button
            onClick={send}
            disabled={busy || !input.trim() || messages.length === 0}
            className="px-4 py-2.5 rounded-xl bg-signal text-cream text-sm font-medium hover:bg-signal-deep transition disabled:opacity-40 shrink-0"
          >
            <Trans>Send</Trans>
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Docks the setup chat: inline in the hero while its slot is on screen, a
 * floating bottom-right widget once the visitor scrolls past it — so the
 * conversation (and any in-flight stream) follows them down the page. ONE
 * component instance throughout; only the wrapper's positioning changes, so
 * chat state survives every transition. Floating mode adds a minimize control
 * that collapses to a launcher pill; scrolling back to the hero always
 * re-docks expanded. On narrow screens floating STARTS minimized — a
 * full-height overlay would swallow the page.
 */
/**
 * Wraps a dock-state change in a View Transition: the chat carries a
 * view-transition-name, so the browser itself morphs it between its layouts
 * (hero slot ↔ corner widget ↔ launcher pill). flushSync makes React commit
 * inside the transition callback — the documented React/Next pattern.
 * Browsers without the API (Firefox) swap instantly. Deliberately NOT gated
 * on prefers-reduced-motion: a one-off functional transition that shows where
 * the chat went, per the film landing's signature-animation product choice.
 */
function withDockTransition(apply: () => void): void {
  if (document.startViewTransition) {
    document.startViewTransition(() => { flushSync(apply); });
  } else {
    apply();
  }
}

/** The floating chat chrome shared by the landing dock and the site-wide
 *  launcher: corner panel sizing + the green launcher pill. */
const FLOATING_PANEL_CLASSES = "fixed bottom-4 right-4 z-50 w-[min(400px,calc(100vw-2rem))] h-[min(70dvh,560px)]";
const LAUNCHER_PILL_CLASSES = "fixed bottom-4 right-4 z-50 flex items-center gap-2.5 rounded-full border border-[#1a2c20] bg-[linear-gradient(120deg,#3d5f45,#233A29_40%,#1a2c20_70%,#3d5f45)] bg-[length:220%_100%] animate-[sqGradientDrift_9s_ease-in-out_infinite] py-2.5 pl-2.5 pr-5 text-sm font-medium text-cream shadow-[0_16px_40px_-12px_rgba(0,0,0,0.5)] transition hover:brightness-110";

export function PreauthChatDock(props: PreauthOnboardingProps) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [floating, setFloating] = useState(false);
  const [minimized, setMinimized] = useState(false);
  // Mirror of the dock state for the observer callback (whose closure would
  // otherwise see the mount-time values) — every state mutation site updates
  // it, so the callback can tell a real change from a no-op and never burn a
  // view transition (or eat a queued real one) committing nothing.
  const dockStateRef = useRef({ floating: false, minimized: false });

  const transitionDock = useCallback((apply: () => void) => { withDockTransition(apply); }, []);

  // Pending re-dock: the morph home must wait for scrolling to settle — a
  // view transition toward an in-flow target that's still moving lands where
  // the slot WAS at capture time, then visibly snaps.
  const redockTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const el = anchorRef.current;
    if (!el) return;
    const cancelRedock = () => {
      if (redockTimer.current) {
        clearTimeout(redockTimer.current);
        redockTimer.current = null;
      }
    };
    const io = new IntersectionObserver(
      ([entry]) => {
        // NOT isIntersecting — that flag is binary (any overlap at all), so it
        // would float only once the slot is fully gone and re-dock the moment
        // a single pixel returns (morphing the panel into a still-offscreen
        // slot). The ratio is the actual "mostly scrolled away" signal.
        const away = entry.intersectionRatio < 0.4;
        cancelRedock();
        const next = {
          floating: away,
          minimized: away && window.matchMedia("(max-width: 640px)").matches,
        };
        // No-op observation (mount, or a threshold crossing that lands on the
        // current state — e.g. nearing the hero while deliberately minimized).
        if (next.floating === dockStateRef.current.floating && next.minimized === dockStateRef.current.minimized) return;
        const apply = () => {
          dockStateRef.current = next;
          setFloating(next.floating);
          setMinimized(next.minimized);
        };
        if (away) {
          transitionDock(apply);
          return;
        }
        // Glide home only once scrollY has held still for a beat; until then
        // the panel simply stays in the corner. Scrolling away again cancels.
        const waitSettled = (lastY: number) => {
          redockTimer.current = setTimeout(() => {
            if (window.scrollY === lastY) transitionDock(apply);
            else waitSettled(window.scrollY);
          }, 120);
        };
        waitSettled(window.scrollY);
      },
      // Callbacks at both edges: 0.4 is the float/re-dock boundary, 0 makes
      // full exit/entry observable too so a fast fling can't skip the boundary
      // without a callback.
      { threshold: [0, 0.4] },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      cancelRedock();
    };
  }, [transitionDock]);

  return (
    // The anchor keeps the hero slot's box (the parent sets an explicit
    // height), so the layout never jumps when the panel goes out of flow.
    <div ref={anchorRef} className="h-full">
      {floating && minimized && (
        // Carries the panel's view-transition-name while it is the chat's
        // visible representation (the panel drops the name when minimized, so
        // the two never hold it at once) — the browser morphs panel ↔ pill.
        <button
          type="button"
          onClick={() => { transitionDock(() => { dockStateRef.current = { ...dockStateRef.current, minimized: false }; setMinimized(false); }); }}
          style={{ viewTransitionName: "preauth-chat" }}
          className={LAUNCHER_PILL_CLASSES}
        >
          <span className="w-7 h-7 rounded-full bg-primary flex items-center justify-center" aria-hidden>
            <SquidlerIcon className="h-4 w-auto text-primary-foreground" />
          </span>
          <Trans>Set up your Hire</Trans>
        </button>
      )}
      {/* Minimized keeps the panel MOUNTED (invisible, never unmounted) so the
          conversation and any in-flight stream survive; the pill above is the
          visible representation then. */}
      <div
        style={{ viewTransitionName: minimized ? undefined : "preauth-chat" }}
        className={
          floating
            ? `${FLOATING_PANEL_CLASSES} ${minimized ? "invisible" : ""}`
            : "h-full"
        }
      >
        <PreauthOnboarding {...props} onMinimize={floating ? () => { transitionDock(() => { dockStateRef.current = { ...dockStateRef.current, minimized: true }; setMinimized(true); }); } : undefined} />
      </div>
    </div>
  );
}

/**
 * Site-wide launcher for the pre-auth setup chat (landing_onboarding
 * "onboarding" arm), mounted once in the root layout for ANONYMOUS visitors
 * and shown on the marketing pages above: a corner pill that expands into the
 * same chat the landing hero embeds. Two deliberate behaviors:
 *
 * - **The chat mounts only on first open.** Mounting starts the LLM opener,
 *   and a pill on every pricing-page view must not burn an anonymous session
 *   per visit. After the first open it stays mounted (invisible when
 *   re-minimized), and the sessionStorage stash means a conversation begun on
 *   the landing continues here seamlessly.
 * - **Visibility resolves client-side.** Layouts can't read searchParams, so
 *   the ?exp_landing_onboarding_v1 preview override is honored here from
 *   location.search on top of the cookie assignment the layout passes in;
 *   analytics enrollment stays cookie-only either way.
 */
export function PreauthChatWidget({ assigned, ...props }: PreauthOnboardingProps & { assigned: boolean }) {
  const pathname = usePathname();
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [everOpened, setEverOpened] = useState(false);

  useEffect(() => {
    const override = new URLSearchParams(window.location.search).get("exp_landing_onboarding_v1");
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot mount read of location.search (external system, no SSR value); the widget must render nothing on the server regardless, so the reveal is post-hydration by design
    setVisible(override === "onboarding" || (assigned && override !== "control"));
  }, [assigned]);

  // usePathname returns the BROWSER path — on the Swedish site that's
  // /sv/pricing etc. (the prefix strip is a server-side rewrite) — so match
  // against the locale-stripped path or the widget vanishes on /sv pages.
  const path = unprefixedPath(pathname);
  const onWidgetPage = WIDGET_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
  if (!visible || !onWidgetPage) return null;

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => { withDockTransition(() => { setEverOpened(true); setOpen(true); }); }}
          style={{ viewTransitionName: "preauth-chat" }}
          className={LAUNCHER_PILL_CLASSES}
        >
          <span className="w-7 h-7 rounded-full bg-primary flex items-center justify-center" aria-hidden>
            <SquidlerIcon className="h-4 w-auto text-primary-foreground" />
          </span>
          <Trans>Set up your Hire</Trans>
        </button>
      )}
      {everOpened && (
        <div
          style={{ viewTransitionName: open ? "preauth-chat" : undefined }}
          className={`${FLOATING_PANEL_CLASSES} ${open ? "" : "invisible"}`}
        >
          <PreauthOnboarding {...props} onMinimize={() => { withDockTransition(() => { setOpen(false); }); }} />
        </div>
      )}
    </>
  );
}
